import styled from '@emotion/styled';
import { DSButton } from '@hundred5/design-system';
import { usePostHog } from 'posthog-js/react';
import React from 'react';

import { getAmplitudeClient } from '@/features/amplitude/utils/amplitude';
import { useWorkspacePermission } from '@/features/permissions';
import { useGenericPricingUpsellModalContext } from '@/features/upsell';
import { usePlanLimits, useTestJobOpeningLimit } from '@/hooks/planLimits';

import { TId } from '@/features/common';
import { TestType } from '@/features/test/types';
import { useCreateCustomTest } from '@/hooks/tests';
import { useParams } from 'react-router';
import { SearchResultsIcon } from './search-results-icon';

export const EmptyState = () => {
  const canCreateJobOpening = useWorkspacePermission()(
    'job openings',
    'create'
  );
  const canAccessFeature = usePlanLimits();
  const canCreateCustomTest = canAccessFeature('create_custom_test');

  const params = useParams<{
    workspace: TId;
    openingId?: TId;
    stageId?: TId;
  }>();

  const { openPricingUpsell } = useGenericPricingUpsellModalContext();
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();
  const canAddTest = useTestJobOpeningLimit();
  const createCustomTest = useCreateCustomTest();

  const handleCreateCustomTest = async () => {
    if (!canCreateCustomTest) {
      openPricingUpsell('unlimitedSkillsTests');
      return;
    }
    if (!canAddTest()) {
      openPricingUpsell('generic');
      return;
    }
    if (!!params.openingId && !!params.stageId && !!params.workspace) {
      createCustomTest(params.openingId, params.stageId, TestType.Quiz);
    }
  };

  return (
    <Container>
      <Header>
        <h3>Search results</h3>
      </Header>
      <Content>
        <SearchResultsIcon />
        <Title>No results for your search</Title>
        <Description>
          You can create a custom test using Toggl's library of over 20,000
          questions or add your own.{' '}
        </Description>
        <ButtonWrapper>
          <DSButton
            variant="primary-purple"
            onClick={() => {
              amplitude?.logEvent('job templates/click no results new test');
              posthog?.capture(
                'hire_admin_app:job templates/click no results new test'
              );
              handleCreateCustomTest();
            }}
            data-rh={
              !canCreateJobOpening
                ? 'Access restricted. Contact workspace admin to change your user rights.'
                : null
            }
          >
            Create a custom test
          </DSButton>
        </ButtonWrapper>
      </Content>
    </Container>
  );
};

const Container = styled.section`
  overflow: hidden;
  color: ${(props) => props.theme.colors.purple[100]};
  padding: 0 36px;

  @media all and (max-width: 768px) {
    padding: 0 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  h3 {
    margin: 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 24px;
`;

const Title = styled.h1`
  color: ${(props) => props.theme.colors.purple[100]};
  font-size: 24px;
  font-weight: 700;
  margin-top: 24px;
`;

const Description = styled.div`
  color: ${(props) => props.theme.colors.purple[60]};
  font-size: 14px;
  font-weight: 400;
  margin-top: 12px;
  width: 480px;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-top: 24px;
`;
